import React, { useCallback, useEffect, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { FiUpload, FiX, FiImage } from "react-icons/fi";

interface DropzoneProps {
  onDrop: (files: File[]) => void;
  accept?: Record<string, string[]>;
  maxFiles?: number;
  maxSize?: number;
  value?: File | File[];
  label?: string;
  description?: string;
  showPreview?: boolean;
  className?: string;
}

export const Dropzone: React.FC<DropzoneProps> = ({
  onDrop,
  accept = { "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"] },
  maxFiles = 1,
  maxSize = 10 * 1024 * 1024, // 10MB default
  value,
  label,
  description,
  showPreview = true,
  className = "",
}) => {
  const handleDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      if (rejectedFiles.length > 0) {
        // Handle rejected files (e.g., show error message)
        console.error("Some files were rejected:", rejectedFiles);
      }
      // Always call onDrop, even with empty array to allow clearing
      onDrop(acceptedFiles);
    },
    [onDrop]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop: handleDrop,
    accept,
    maxFiles,
    maxSize,
    multiple: maxFiles > 1,
    noClick: false,
    noKeyboard: false,
  });

  // Override onClick to prevent file dialog when clicking delete button
  const rootProps = getRootProps();
  const originalOnClick = rootProps.onClick;
  rootProps.onClick = (e: React.MouseEvent<HTMLElement>) => {
    // Check if the click target is a delete button or its child
    const target = e.target as HTMLElement;
    const isDeleteButton = target.closest('[data-delete-button]');
    if (isDeleteButton) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (originalOnClick) {
      originalOnClick(e as any);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  // Create preview URLs and clean them up
  const previewUrls = useMemo(() => {
    if (!value) return [];
    
    const files = maxFiles === 1 ? [value as File] : (value as File[]);
    return files.map(file => URL.createObjectURL(file));
  }, [value, maxFiles]);

  // Cleanup object URLs on unmount or when files change
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const hasFiles = maxFiles === 1
    ? value !== null && value !== undefined
    : ((value as File[] | undefined)?.length ?? 0) > 0;

  const cardStyle: React.CSSProperties = {
    backgroundColor: isDragActive
      ? isDragReject
        ? "#fef2f2"
        : "#fafafa"
      : "#ffffff",
    border: isDragActive
      ? isDragReject
        ? "2px solid #ef4444"
        : "2px solid #09090b"
      : "1px solid #e4e4e7",
    borderRadius: "0.75rem",
    padding: "1.25rem",
    minHeight: !hasFiles ? "240px" : undefined,
    display: !hasFiles ? "flex" : "block",
    alignItems: !hasFiles ? "center" : undefined,
    justifyContent: !hasFiles ? "center" : undefined,
    boxShadow: isDragActive
      ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)"
      : "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
    transition: "all 0.2s ease",
    cursor: "pointer",
  };

  const renderSingleFile = () => {
    const file = value as File | undefined;
    if (file && showPreview && previewUrls.length > 0) {
      const previewUrl = previewUrls[0];
      return (
        <div style={{ position: "relative", width: "100%" }}>
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "300px",
              margin: "0 auto",
              borderRadius: "0.5rem",
              overflow: "hidden",
            }}
          >
            <img
              src={previewUrl}
              alt={file.name}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "cover",
              }}
            />
            <div
              data-delete-button
              style={{
                position: "absolute",
                top: "0.5rem",
                right: "0.5rem",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                borderRadius: "50%",
                width: "2rem",
                height: "2rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDrop([]);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.9)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
              }}
            >
              {/* @ts-ignore - react-icons type issue */}
              <FiX size={16} color="white" />
            </div>
          </div>
          <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
            <p style={{ fontSize: "0.875rem", color: "#10b981", fontWeight: "500" }}>
              ✓ {file.name}
            </p>
            <p style={{ fontSize: "0.75rem", color: "#71717a", marginTop: "0.25rem" }}>
              {formatFileSize(file.size)} • Click to change
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderMultipleFiles = () => {
    const files = value as File[] | undefined;
    if (files && files.length > 0 && showPreview && previewUrls.length > 0) {
      return (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              gap: "0.75rem",
              marginBottom: "1rem",
            }}
          >
            {files.map((file, index) => {
              const previewUrl = previewUrls[index];
              return (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    aspectRatio: "1",
                  }}
                >
                  <img
                    src={previewUrl}
                    alt={file.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    data-delete-button
                    style={{
                      position: "absolute",
                      top: "0.25rem",
                      right: "0.25rem",
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      borderRadius: "50%",
                      width: "1.5rem",
                      height: "1.5rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const newFiles = files.filter((_, i) => i !== index);
                      onDrop(newFiles);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.9)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
                    }}
                  >
                    {/* @ts-ignore - react-icons type issue */}
                    <FiX size={12} color="white" />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: "0.875rem", color: "#10b981", fontWeight: "500" }}>
              ✓ {files.length} image{files.length !== 1 ? "s" : ""} selected
            </p>
            <p style={{ fontSize: "0.75rem", color: "#71717a", marginTop: "0.25rem" }}>
              {files.length < maxFiles ? "Click to add more" : "Maximum reached"}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={className}>
      <div
        {...rootProps}
        style={cardStyle}
        onMouseEnter={(e) => {
          if (!isDragActive) {
            e.currentTarget.style.borderColor = "#a1a1aa";
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragActive) {
            e.currentTarget.style.borderColor = "#e4e4e7";
          }
        }}
      >
        <input {...getInputProps()} />
        {hasFiles && showPreview ? (
          maxFiles === 1 ? (
            renderSingleFile()
          ) : (
            renderMultipleFiles()
          )
        ) : (
          <div style={{ textAlign: "center", padding: "1.5rem 1rem", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              {isDragActive ? (
                // @ts-ignore - react-icons type issue
                <FiUpload
                  size={48}
                  color={isDragReject ? "#ef4444" : "#09090b"}
                  style={{
                    animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                  }}
                />
              ) : (
                // @ts-ignore - react-icons type issue
                <FiImage size={48} color="#71717a" />
              )}
            </div>
            {isDragActive ? (
              <div>
                <p
                  style={{
                    fontSize: "0.875rem",
                    color: isDragReject ? "#ef4444" : "#09090b",
                    fontWeight: "500",
                    marginBottom: "0.25rem",
                  }}
                >
                  {isDragReject
                    ? "File type not supported"
                    : "Drop your file here"}
                </p>
                {!isDragReject && (
                  <p style={{ fontSize: "0.75rem", color: "#71717a" }}>
                    Release to upload
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p
                  style={{
                    fontSize: "0.875rem",
                    color: "#71717a",
                    marginBottom: "0.25rem",
                  }}
                >
                  {label || (
                    <>
                      <span style={{ fontWeight: "500", color: "#09090b" }}>
                        Click to upload
                      </span>{" "}
                      or drag and drop
                    </>
                  )}
                </p>
                <p style={{ fontSize: "0.75rem", color: "#a1a1aa" }}>
                  {description ||
                    `PNG, JPG, GIF up to ${formatFileSize(maxSize)}`}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

